import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { AppInsightsService } from '../appInsights/appInsights.service';
import { LocalCacheService } from '../cache/localCache.service';
import { Enums, IEnums } from '../../enums/enums';
import { environment } from '../../../../environments/environment';
import * as Models from '../../models/models-index';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { isLoggedIn, selectLoggedInUser, selectRouter } from '../../../_store/app.selectors';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../_store/app-state.model';
import { ConfigurationService, RuntimeConfigService } from '../services-index';
import { AppActions } from '../../../_store/action-types';
import { UsersService } from '../api/api-index';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private enums: IEnums = Enums;
  private serviceBase = undefined;
  isLoggedIn$ = this.store.pipe(select(isLoggedIn));
  loggedInUser$ = this.store.pipe(select(selectLoggedInUser));
  routeParams$ = this.store.pipe(select(selectRouter));

  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private cache: LocalCacheService,
    private configService: RuntimeConfigService,
    private appInsights: AppInsightsService,
    private usersService: UsersService) {
    this.serviceBase = environment.baseApiUri;
  }

  getAuthToken(): string {
    return localStorage.getItem('sd_unified_access_token');
  }

  getUser(): Models.IAuthenticationInfo {
    const userString = localStorage.getItem('user');
    return userString ? <Models.IAuthenticationInfo>JSON.parse(userString) : null;
  }

  getUserDetails(): Models.ReportingUser {
    if (!localStorage.getItem('userDetails'))
      return null;

    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? <Models.ReportingUser>JSON.parse(userDetailsString) : null;
    return userDetails;
  }

  //TODO: First chance we get in 5.3 or later, we should work to get rid of IAuthenticationInfo so we decouple the token from the user representation
  loginCustomToken(customToken: string, refreshToken: string): Observable<Models.IAuthenticationInfo> {
    const payload = this.decodeJwtPayload(customToken);
    const userId = (payload.sub ?? '').toString();
    const expireDate = new Date(((payload.exp as number) ?? 0) * 1000);
    const rolesFromClaims = this.extractArrayClaim(payload, ['role', 'roles', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
    const permissionsFromClaims = this.extractArrayClaim(payload, ['permission', 'permissions', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/permission']);
    const roleEntityFromClaims = this.extractSingleClaim(payload, ['role_entity', 'roleEntity', 'http://schemas.reporting/role_entity']);
    const fallbackAuth = this.buildAuthFromClaims(payload, customToken, expireDate, rolesFromClaims, permissionsFromClaims);
    const fallbackUserDetails = this.buildUserDetailsFromClaims(payload, fallbackAuth, rolesFromClaims, permissionsFromClaims);
    const tokenUsage = (payload.token_usage ?? payload.tokenUsage ?? payload.TokenUsage ?? '').toString().toLowerCase();
    const tokenUsageTelemetry = tokenUsage || 'user';

    this.resetUser();

    localStorage.setItem('sd_unified_access_token', customToken);
    if (refreshToken) {
      localStorage.setItem('sd_unified_refresh_token', refreshToken);
    }

    console.log('loginCustomToken', customToken, refreshToken);

    if (!userId) {
      console.warn('JWT token missing subject claim; falling back to claim-derived user info.');
      this.persistAuthState(fallbackAuth, fallbackUserDetails);
      this.appInsights.trackEvent('LoginSuccess', {
        props: {
          UserName: fallbackAuth.userName,
          RoleEntity: fallbackAuth.roleEntity,
          UserRole: fallbackAuth.role,
          TokenUsage: tokenUsageTelemetry,
          Source: 'ClaimsFallback'
        }
      });
      return of(fallbackAuth);
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${customToken}`
    };

    return this.usersService.getUser(userId, headers).pipe(
      tap(response => {
        if (!response.role && rolesFromClaims.length === 0) {
          throw new Error('Role is missing in both the response and token claims.');
        }
      }),
      map(response => {
        const baselineRoles = rolesFromClaims.length > 0
          ? this.buildRolesFromClaims(response.roles, rolesFromClaims, permissionsFromClaims)
          : this.cloneRoles(response.roles);

        const primaryRoleName = (rolesFromClaims.length > 0
          ? rolesFromClaims[0]
          : (response.role?.name ?? fallbackAuth.role ?? 'Impersonated')) || 'Impersonated';

        const roleEntity = roleEntityFromClaims ?? response.roleEntity ?? fallbackAuth.roleEntity ?? '';

        const baselinePermissions = permissionsFromClaims.length > 0
          ? Array.from(new Set(permissionsFromClaims))
          : this.collectPermissionNames(baselineRoles);

        let finalRoles = rolesFromClaims.length > 0
          ? this.buildRolesFromClaims(response.roles, rolesFromClaims, baselinePermissions)
          : baselineRoles;

        if (!finalRoles.some(role => (role.name ?? '').toLowerCase() === primaryRoleName.toLowerCase())) {
          finalRoles = [
            ...finalRoles,
            {
              name: primaryRoleName,
              permissions: baselinePermissions.length > 0
                ? this.buildClaimPermissionObjects(baselinePermissions)
                : []
            }
          ];
        }

        if (finalRoles.length === 0) {
          finalRoles = [{
            name: primaryRoleName,
            permissions: baselinePermissions.length > 0
              ? this.buildClaimPermissionObjects(baselinePermissions)
              : []
          }];
        }

        const primaryRolePermissions = permissionsFromClaims.length > 0
          ? this.buildClaimPermissionObjects(baselinePermissions)
          : this.clonePermissions(
            finalRoles.find(role => (role.name ?? '').toLowerCase() === primaryRoleName.toLowerCase())?.permissions
          );

        finalRoles = finalRoles.map(role => {
          if ((role.name ?? '').toLowerCase() === primaryRoleName.toLowerCase()) {
            return {
              name: primaryRoleName,
              permissions: this.clonePermissions(primaryRolePermissions)
            };
          }

          return {
            name: role.name,
            permissions: this.clonePermissions(role.permissions)
          };
        });

        const permissionsForAuth = baselinePermissions.length > 0
          ? baselinePermissions
          : this.collectPermissionNames(finalRoles);

        const userPrimaryRole = finalRoles.find(role => (role.name ?? '').toLowerCase() === primaryRoleName.toLowerCase())
          ?? finalRoles[0];

        const auth: Models.IAuthenticationInfo = {
          isAuthenticated: true,
          userName: response.userName,
          userId: response.id,
          firstName: response.firstName,
          lastName: response.lastName,
          token: customToken,
          role: userPrimaryRole?.name ?? primaryRoleName,
          roles: Array.from(new Set(finalRoles.map(r => r.name).filter(name => !!name))).join(','),
          roleIds: '',
          orgLookupTypeIds: '',
          roleEntity,
          dealerGroup: '',
          clientUserName: response.userName,
          userSettings: JSON.stringify(response.settings ?? {}),
          locale: response.settings?.locale ?? fallbackAuth.locale,
          expireDate: expireDate,
          permissions: permissionsForAuth
        };

        const updatedUser: Models.ReportingUser = {
          ...response,
          roleEntity,
          roles: finalRoles,
          role: {
            name: auth.role,
            permissions: this.clonePermissions(userPrimaryRole?.permissions)
          }
        };

        this.persistAuthState(auth, updatedUser);

        this.appInsights.trackEvent('LoginSuccess', {
          props: {
            UserName: auth.userName,
            RoleEntity: auth.roleEntity,
            UserRole: auth.role,
            TokenUsage: tokenUsageTelemetry,
            Source: 'UserService'
          }
        });

        return auth;
      }),
      catchError(err => {
        console.error('Failed to retrieve user profile for token login', err);
        this.appInsights.trackEvent('LoginError', {
          props: { customToken: customToken, error: JSON.stringify(err) }
        });

        this.persistAuthState(fallbackAuth, fallbackUserDetails);

        this.appInsights.trackEvent('LoginSuccess', {
          props: {
            UserName: fallbackAuth.userName,
            RoleEntity: fallbackAuth.roleEntity,
            UserRole: fallbackAuth.role,
            TokenUsage: tokenUsageTelemetry,
            Source: 'ClaimsFallback'
          }
        });

        return of(fallbackAuth);
      })
    );
  }

  impersonate(accessToken: string, role: string, roleEntity?: string, userId?: string): Observable<{ accessToken: string; expiresAtUtc: string; tokenType: string }> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    const body: any = { role };

    if (roleEntity) {
      body.roleEntity = roleEntity;
    }

    if (userId) {
      body.userId = userId;
    }

    console.log('Impersonation request', body);

    return this.http.post<{ accessToken: string; expiresAtUtc: string; tokenType: string }>(
      `${environment.baseV5ApiUri}/auth/impersonate`, //baseV5ApiUri already includes /api
      body,
      { headers }
    ).pipe(
      tap(() => {
        this.appInsights.trackEvent('ImpersonationSuccess', {
          props: {
            role,
            roleEntity: roleEntity || 'n/a'
          }
        });
      }),
      catchError(err => {
        this.appInsights.trackEvent('ImpersonationError', {
          props: {
            role,
            roleEntity: roleEntity || 'n/a',
            error: JSON.stringify(err)
          }
        });
        return throwError(() => err);
      })
    );
  }

  refreshCustomToken(refreshToken: string): Observable<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    return this.http.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
      `${environment.baseV5ApiUri}/api/auth/refresh`,
      { refreshToken }
    );
  }

  resetUser() {
    localStorage.removeItem('user');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('sd_unified_access_token');
    localStorage.removeItem('sd_unified_refresh_token');
  }

  register(user: Models.IUser): Observable<any> {
    return this.http.post(this.serviceBase + '/account/register', user)
      .pipe(
        catchError(err => {
          appInsights.trackException(err);
          this.handleError(err)
          return of(err);
        })
      );
  }

  exchangeEntraToken(entraToken: string): Observable<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    const body = { token: entraToken };
    console.log('exchangeEntraToken', body);
    return this.http.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
      `${environment.baseV5ApiUri}/auth/entra`,
      body
    ).pipe(catchError(err => { console.error('error exchanging token', err); return throwError(() => err); }));
  }

  /**
   * Exchanges a B2C token for a unified authentication token with our backend
   * @param token the ID token from Azure B2C
   * @returns Observable with access token, refresh token and expiry
   */
  exchangeB2CToken(token: string): Observable<{ accessToken: string; refreshToken: string; expiresIn: number }> {
    this.appInsights.trackEvent('B2C Token Exchange', { props: { attempt: 'started' } });
    const body = { token };
    console.log('exchangeB2CToken', body);

    return this.http.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
      `${environment.baseV5ApiUri}/auth/b2c-token`,
      body
    ).pipe(
      tap(response => {
        this.appInsights.trackEvent('B2CTokenExchange Success', {
          props: {
            success: 'true'
          }
        });
      }),
      catchError(err => {
        console.error('error exchanging B2C token', err);
        this.appInsights.trackEvent('B2CTokenExchange Error', {
          props: { error: JSON.stringify(err) }
        });
        return throwError(() => err);
      })
    );
  }

  changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Observable<any> {
    return this.http.post(this.serviceBase + '/account/changepassword', {
      oldPassword: oldPassword,
      password: newPassword,
      confirmPassword: confirmPassword
    }).pipe(
      catchError(err => {
        appInsights.trackException(err);
        this.handleError(err)
        return of(err);
      })
    );
  }

  resetPassword(userName: string, newPassword: string, confirmPassword: string): Observable<any> {
    return this.http.post(this.serviceBase + '/account/resetpassword', {
      userName: userName,
      password: newPassword,
      confirmPassword: confirmPassword
    }).pipe(
      catchError(err => {
        appInsights.trackException(err);
        this.handleError(err)
        return of(err);
      })
    );
  }

  authenticate(userName: string, password: string, roleEntity?: string, dealerGroup?: string): Observable<Models.IAuthenticationInfo> {
    const data = 'grant_type=password&username=' + encodeURIComponent(userName) + '&password=' + encodeURIComponent(password);
    const headers = new HttpHeaders();
    const dealerGroupParameter = !!dealerGroup ? `?dealerGroup=${dealerGroup}` : '';
    headers.append('Content-Type', 'application/x-www-form-urlencoded');


    return this.http.post<any>(environment.authUri + dealerGroupParameter, data, { headers: headers }).pipe(
      tap(response => {
        this.appInsights.trackEvent('LoginSuccess', {
          props: {
            UserName: userName,
            RoleEntity: roleEntity || response.roleEntity,
            UserRole: response.roleIds,
          }
        });
      }),
      map(response => {
        const authenticationData: Models.IAuthenticationInfo = {
          isAuthenticated: true,
          userName: userName,
          userId: response.userId.toString(),
          firstName: response.firstName,
          lastName: response.lastName,
          token: response.access_token,
          role: response.roles,
          roles: response.roles,
          roleIds: response.roleIds,
          orgLookupTypeIds: response.orgLookupTypeIds,
          roleEntity: roleEntity || response.roleEntity,
          dealerGroup: response.dealerGroup,
          clientUserName: response.userName,
          userSettings: response.userSettings,
          locale: response.locale,
          expireDate: new Date(response['.expires']),
        };

        localStorage.setItem('user', JSON.stringify(authenticationData));

        return authenticationData;
      }),
      catchError(err => {
        this.appInsights.trackEvent('LoginError', {
          props: { userName: userName, error: JSON.stringify(err) }
        });
        return throwError(() => err);
      })
    );
  }

  /**
   * Authenticates the user with Azure B2C
   * @param userName The username of the user
   * @param password The password of the user
   * @returns Observable with authentication info
   */
  authenticateWithB2C(userName: string, password: string): Observable<Models.IAuthenticationInfo> {
    const data = {
      username: userName,
      password: password
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<any>(`${environment.baseV5ApiUri}/auth/b2c`, data, { headers: headers }).pipe(
      tap(response => {
        this.appInsights.trackEvent('B2CLoginSuccess', {
          props: {
            UserName: userName,
            RoleEntity: response.roleEntity,
            UserRole: response.role
          }
        });
      }),
      map(response => {
        const auth: Models.IAuthenticationInfo = {
          isAuthenticated: true,
          userName: userName,
          userId: response.userId.toString(),
          firstName: response.firstName,
          lastName: response.lastName,
          token: response.accessToken,
          role: response.role,
          roles: response.roles,
          roleIds: response.roleIds,
          orgLookupTypeIds: response.orgLookupTypeIds,
          roleEntity: response.roleEntity,
          dealerGroup: response.dealerGroup,
          clientUserName: response.clientUserName || userName,
          userSettings: response.userSettings,
          locale: response.locale,
          expireDate: new Date(Date.now() + response.expiresIn * 1000)
        };

        localStorage.setItem('user', JSON.stringify(auth));
        localStorage.setItem('sd_unified_access_token', response.accessToken);
        localStorage.setItem('sd_unified_refresh_token', response.refreshToken);

        return auth;
      }),
      catchError(err => {
        this.appInsights.trackEvent('B2CLoginError', {
          props: { userName: userName, error: JSON.stringify(err) }
        });
        return throwError(() => err);
      })
    );
  }

  /**
   * Initiates the SSO login flow by requesting an AuthN request from the API
   * For clients like Infiniti, this will return a URL that the user needs to be redirected to
   * @param provider The client provider ID
   */
  ssoProviderLogin(provider: string): Observable<{ authRequest: string }> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    return this.http.post<{ authRequest: string }>(`${environment.baseV5ApiUri}/auth/clientssologin`, {}, { headers }).pipe(
      tap(response => {
        this.appInsights.trackEvent('SsoLoginInitiated', {
          props: {
            Provider: provider
          }
        });
      }),
      catchError(err => {
        this.appInsights.trackEvent('SsoLoginInitiationError', {
          props: {
            provider: provider,
            error: JSON.stringify(err)
          }
        });
        return throwError(() => err);
      })
    );
  }

  /**
   * Exchanges a client assertion (SAML response) for a custom token
   * This is used after the user completes the SSO flow with the client IDP
   * @param clientAssertion The SAML response from the client IDP
   */
  exchangeClientAssertion(clientAssertion: string): Observable<Models.IAuthenticationInfo> {
    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json');

    // Step 1: Exchange the SAML response for a unified token
    return this.http.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
      `${environment.baseV5ApiUri}/api/auth/clientsso`,
      clientAssertion,
      { headers }
    ).pipe(
      tap(response => {
        // Store tokens in local storage
        localStorage.setItem('sd_unified_access_token', response.accessToken);
        localStorage.setItem('sd_unified_refresh_token', response.refreshToken);

        this.appInsights.trackEvent('ClientAssertionExchanged', {
          props: {
            success: 'true'
          }
        });
      }),
      switchMap(response => {
        // Step 2: Parse the JWT to get user ID
        const jwt = response.accessToken.split('.')[1];
        const jwtData = JSON.parse(atob(jwt));
        const userId = jwtData.sub;
        const expireDate = new Date(jwtData.exp * 1000);

        // Step 3: Get user details from the user service
        // Build headers with the token
        const userHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${response.accessToken}`
        };

        return this.usersService.getUser(userId, userHeaders).pipe(
          map(userResponse => {
            // Step 4: Create authentication info object
            const auth: Models.IAuthenticationInfo = {
              isAuthenticated: true,
              userName: userResponse.userName,
              userId: userResponse.id,
              firstName: userResponse.firstName,
              lastName: userResponse.lastName,
              token: response.accessToken,
              role: userResponse.role?.name,
              roles: userResponse.roles.map(r => r.name).join(','),
              roleIds: undefined,
              orgLookupTypeIds: undefined,
              roleEntity: userResponse.roleEntity,
              dealerGroup: undefined,
              clientUserName: userResponse.userName,
              userSettings: JSON.stringify(userResponse.settings),
              locale: userResponse.settings?.locale,
              expireDate: expireDate,
              permissions: userResponse.roles.map(r => r.permissions.map(p => p.name)).reduce((acc, val) => acc.concat(val), [])
            };

            // Store user info in localStorage
            localStorage.setItem('user', JSON.stringify(auth));
            localStorage.setItem('userDetails', JSON.stringify(userResponse));

            return auth;
          })
        );
      }),
      catchError(err => {
        this.appInsights.trackEvent('ClientAssertionExchangeError', {
          props: {
            error: JSON.stringify(err)
          }
        });
        return throwError(() => err);
      })
    );
  }

  ssoLogin(response: any, roleEntity: string, firstName: string, lastName: string, clientUserName?: string, userSettings?: string): any {
    this.cache.purgeAllLocalCaches();
    const authenticationData: Models.IAuthenticationData = {
      userName: response.userName,
      userId: response.userId.toString(),
      firstName: firstName,
      lastName: lastName,
      token: response.access_token,
      role: response.roles,
      roles: response.roles,
      roleIds: response.roleIds,
      orgLookupTypeIds: response.orgLookupTypeIds,
      roleEntity: roleEntity,
      clientUserName: clientUserName,
      dealerGroup: response.dealerGroup,
      expireDate: new Date(response['.expires'])
    };

    return authenticationData;

  }

  updateAuthenticationInfo(newInfo: Models.IAuthenticationInfo, force?: boolean) {
    // if (force || !this.authenticationInfo || JSON.stringify(this.authenticationInfo) !== JSON.stringify(newInfo)) {
    // 	this.authenticationInfo = newInfo;
    // 	this.authInfoSubject.next(newInfo);
    // 	this.authInfoChanged.next(true);
    // }
  }

  updateAuthenticationInfoExternal(newInfo: Models.IAuthenticationInfo) {
    this.updateAuthenticationInfo(newInfo, true);

    const authenticationData: Models.IAuthenticationData = {
      userName: newInfo.userName,
      userId: newInfo.userId.toString(),
      firstName: newInfo.firstName,
      lastName: newInfo.lastName,
      token: newInfo.token,
      role: newInfo.roles,
      roles: newInfo.roles,
      roleIds: newInfo.roleIds,
      orgLookupTypeIds: newInfo.orgLookupTypeIds,
      roleEntity: newInfo.roleEntity,
      clientUserName: newInfo.clientUserName,
      dealerGroup: newInfo.dealerGroup,
      expireDate: newInfo.expireDate,
    };

    this.cache.setLocalCache<Models.IAuthenticationData>(this.enums.cacheKeys.authData, authenticationData);
  }

  // updateStoredUserNames(firstName: string, lastName: string) {
  // 	const currentAuthInfo = this.getAuthenticationInfo();
  // 	currentAuthInfo.firstName = firstName;
  // 	currentAuthInfo.lastName = lastName;
  // 	this.updateAuthenticationInfoExternal(currentAuthInfo);
  // }

  updateUser(user: Models.IUser): Observable<any> {
    return this.http.post(this.serviceBase + '/account/updateUser', user)
      .pipe(
        catchError(err => {
          appInsights.trackException(err);
          return this.handleError(err);
        })
      );
  }

  private decodeJwtPayload(token: string): any {
    try {
      const segments = token.split('.');
      const payloadSegment = segments[1] ?? '';
      const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to decode JWT payload', error);
      return {};
    }
  }

  private extractArrayClaim(payload: any, keys: string[]): string[] {
    for (const key of keys) {
      const value = payload?.[key] ?? payload?.[key.toLowerCase()] ?? payload?.[key.toUpperCase()];

      if (value === undefined || value === null) {
        continue;
      }

      const collection = Array.isArray(value) ? value : [value];
      const normalized = collection
        .map(entry => entry === undefined || entry === null ? '' : entry.toString())
        .filter(entry => entry.length > 0);

      if (normalized.length > 0) {
        return Array.from(new Set(normalized));
      }
    }

    return [];
  }

  private extractSingleClaim(payload: any, keys: string[]): string | undefined {
    for (const key of keys) {
      const value = payload?.[key] ?? payload?.[key.toLowerCase()] ?? payload?.[key.toUpperCase()];
      if (value === undefined || value === null) {
        continue;
      }

      const normalized = value.toString().trim();
      if (normalized.length > 0) {
        return normalized;
      }
    }

    return undefined;
  }

  private cloneRoles(roles: Models.ReportingRole[]): Models.ReportingRole[] {
    if (!roles || roles.length === 0) {
      return [];
    }

    return roles.map(role => ({
      name: role.name,
      permissions: this.clonePermissions(role.permissions)
    }));
  }

  private clonePermissions(permissions: Models.ReportingPermission[] | undefined): Models.ReportingPermission[] {
    if (!permissions || permissions.length === 0) {
      return [];
    }

    return permissions.map(permission => ({ ...permission }));
  }

  private buildRolesFromClaims(
    existingRoles: Models.ReportingRole[],
    claimRoles: string[],
    claimPermissions: string[]
  ): Models.ReportingRole[] {
    if (claimRoles.length === 0) {
      return this.cloneRoles(existingRoles);
    }

    const normalizedExisting = existingRoles ?? [];

    return claimRoles.map(roleName => {
      const match = normalizedExisting.find(r => r.name?.toLowerCase() === roleName.toLowerCase());
      const permissions = claimPermissions.length > 0
        ? this.buildClaimPermissionObjects(claimPermissions)
        : this.clonePermissions(match?.permissions);

      return {
        name: roleName,
        permissions
      };
    });
  }

  private buildClaimPermissionObjects(names: string[]): Models.ReportingPermission[] {
    if (!names || names.length === 0) {
      return [];
    }

    return Array.from(new Set(names.map(name => name ?? '').filter(name => name.length > 0))).map(name => ({
      name,
      category: 'Impersonated'
    }));
  }

  private collectPermissionNames(roles: Models.ReportingRole[]): string[] {
    const names = roles
      .flatMap(role => (role.permissions ?? []).map(permission => permission.name))
      .filter(name => !!name) as string[];

    return Array.from(new Set(names));
  }

  private buildAuthFromClaims(
    payload: any,
    token: string,
    expireDate: Date,
    roles: string[],
    permissions: string[]
  ): Models.IAuthenticationInfo {
    const userName = (payload.name ?? payload.unique_name ?? payload.preferred_username ?? payload.email ?? '') as string;
  const firstName = (payload.given_name ?? payload.givenName ?? payload.firstName ?? payload.givenname ?? '') as string;
  const lastName = (payload.family_name ?? payload.familyName ?? payload.lastName ?? payload.familyname ?? '') as string;
    const roleEntity = (payload.role_entity ?? payload.roleEntity ?? '') as string;
    const locale = (payload.locale ?? 'en-US') as string;

    const uniquePermissions = Array.from(new Set(permissions));

    return {
      isAuthenticated: true,
      userName,
      userId: (payload.sub ?? '') as string,
      firstName,
      lastName,
      token,
      role: roles[0] ?? '',
      roles: roles.join(','),
      roleEntity,
      dealerGroup: '',
      roleIds: '',
      orgLookupTypeIds: '',
      userSettings: '{}',
      locale,
      clientUserName: userName,
      expireDate,
      permissions: uniquePermissions
    };
  }

  private buildUserDetailsFromClaims(
    payload: any,
    auth: Models.IAuthenticationInfo,
    roles: string[],
    permissions: string[]
  ): Models.ReportingUser {
    const rolesWithPermissions = roles.map(roleName => ({
      name: roleName,
      permissions: this.buildClaimPermissionObjects(permissions)
    }));

    const fallbackPermissions = permissions.length > 0 ? permissions : auth.permissions ?? [];

    const effectiveRoles = rolesWithPermissions.length > 0 ? rolesWithPermissions : [{
      name: auth.role || 'Impersonated',
      permissions: this.buildClaimPermissionObjects(fallbackPermissions)
    }];

    const primaryRole = effectiveRoles[0] ?? {
      name: auth.role || 'Impersonated',
      permissions: this.buildClaimPermissionObjects(fallbackPermissions)
    };

    return {
      id: auth.userId,
      externalId: (payload.oid ?? payload.sub ?? auth.userId) as string,
      authProvider: (payload.auth_provider ?? 'Impersonation') as string,
      userName: auth.userName,
      emailAddress: (payload.email ?? payload.preferred_username ?? auth.userName) as string,
      firstName: auth.firstName,
      lastName: auth.lastName,
      title: '',
      createdAt: undefined,
      lastLogin: undefined,
      roles: effectiveRoles,
      roleEntity: auth.roleEntity,
      isSystem: Boolean(payload.is_system ?? payload.isSystem ?? false),
      role: {
        name: primaryRole.name,
        permissions: this.clonePermissions(primaryRole.permissions)
      },
      settings: {
        locale: auth.locale || 'en-US',
        theme: 'default',
        timeZone: 'UTC'
      }
    };
  }

  private persistAuthState(auth: Models.IAuthenticationInfo, userDetails: Models.ReportingUser | any): void {
    localStorage.setItem('user', JSON.stringify(auth));
    if (userDetails) {
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
    } else {
      localStorage.removeItem('userDetails');
    }

    this.store.dispatch(AppActions.login({ auth }));
  }

  private handleError(err: any) {
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = `Backend returned code ${err.status}: ${err.body.error}`;
    }
    this.appInsights.trackException(err);
    console.error(err);
    return throwError(() => errorMessage);
  }

  /**
   * Processes the authentication response from the SSO service
   * This is used by the ssologinV2.component when the .NET host redirects with SAML assertion data
   * @param response The response from the SSO authentication
   * @returns Authentication info object
   */
  getAuthInfo(response: any): Models.IAuthenticationInfo {
    // Store the access token in local storage
    localStorage.setItem('sd_unified_access_token', response.access_token);

    // Parse the token expiration date
    const expiresDate = new Date(response['.expires']);

    // Build the authentication info object
    const authenticationData: Models.IAuthenticationInfo = {
      isAuthenticated: true,
      userName: response.userName,
      userId: response.userId,
      firstName: response.firstName,
      lastName: response.lastName,
      token: response.access_token,
      role: response.roles,
      roles: response.roles,
      roleIds: response.roleIds,
      orgLookupTypeIds: response.orgLookupTypeIds,
      roleEntity: response.roleEntity,
      dealerGroup: response.dealerGroup,
      clientUserName: response.userName,
      userSettings: response.userSettings,
      locale: response.locale,
      expireDate: expiresDate,
      permissions: response.permissions || []
    };

    // Store the auth data in local storage
    localStorage.setItem('user', JSON.stringify(authenticationData));

    // Track successful login
    this.appInsights.trackEvent('SsoLoginSuccess', {
      props: {
        UserName: authenticationData.userName,
        RoleEntity: authenticationData.roleEntity,
        UserRole: authenticationData.role,
      }
    });

    return authenticationData;
  }
}
